import { useState, useMemo, useRef } from "react";
import { useTranslate } from "@refinedev/core";

import { usePaginatedTable, useQueryParams } from "@shared/hooks";
import { ModalRef } from "@shared/components";
import { Subscription } from "@modules/subscriptions/types";
import { useFetchResourcesByIdsQuery } from "@shared/services/queries";
import { ApiResponse } from "@shared/types";
import { Plan } from "@modules/plans/types";
import { User } from "@modules/users/types";

export function useSubscriptionList() {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const createSubscriptionModalRef = useRef<ModalRef>(null);
  const editSubscriptionModalRef = useRef<ModalRef>(null);

  const translate = useTranslate();
  const { getQueryParams } = useQueryParams();

  const { rows, loading, refetch, ...tableProps } = usePaginatedTable<Subscription>({
    resource: "v1/subscription_admin",
    filters: getQueryParams(),
  });

  const userIds = useMemo(
    () => [...new Set(rows?.map((prescriber) => prescriber.user).filter((id): id is string => !!id))],
    [rows]
  );

  const { data: users } = useFetchResourcesByIdsQuery<ApiResponse<User>>({
    resource: "v1/user_admin",
    ids: userIds,
  });

  const plansIds = useMemo(
    () => [...new Set(rows?.map((subscription) => subscription.plan).filter((id): id is number => !!id))],
    [rows]
  );

  const { data: plans } = useFetchResourcesByIdsQuery<ApiResponse<Plan>>({
    resource: "v1/subscription_admin",
    ids: plansIds,
  });

  const dataToExport = useMemo(() => {
    return selectedRows.length > 0
      ? rows.filter((row) => selectedRows.includes(row.id))
      : rows;
  }, [selectedRows, rows]);

  return {
    rows,
    loading,
    tableProps,
    selectedRows,
    dataToExport,
    createSubscriptionModalRef,
    editSubscriptionModalRef,
    plans: plans?.results,
    users: users?.results,
    refetch,
    setSelectedRows,
    translate,
  };
}
